import React, { useEffect, useState } from "react";
import { Box, Grid, Heading, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import SEO from "./SEO";
import MaskedReveal from "./MaskedReveal";
import TimelineComponent from "./TimelineComponent";
import timeline from "../contents/timeline";
import { getAssetLanguageKey, normalizeSiteLanguage } from "../i18n/language";

const About: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [timelineItems, setTimelineItems] = useState<any[]>([]);

  useEffect(() => {
    const activeLanguage = i18n.resolvedLanguage || i18n.language;
    const currentLang = getAssetLanguageKey(activeLanguage);
    const siteLanguage = normalizeSiteLanguage(activeLanguage);

    setTimelineItems(
      timeline.map((item) => ({
        title: t(item.dateKey),
        cardTitle: t(item.titleKey),
        cardDetailedText: t(item.contentKey),
        link: item.link,
        img:
          item.imgBySiteLanguage?.[siteLanguage] ||
          item.imgByLang?.[currentLang] ||
          item.img,
        video: item.video,
      }))
    );
  }, [i18n.language, i18n.resolvedLanguage, t]);

  return (
    <Box
      as="section"
      overflow="hidden"
      bg="rgba(255,255,255,0.72)"
    >
      <SEO title={t("seo.pages.about.title")} description={t("seo.pages.about.description")} canonical="https://deepsoulai.com/about" />
      <Box
        data-testid="about-intro"
        maxW="1400px"
        mx="auto"
        px={{ base: 4, md: 8 }}
        pt={{ base: 14, md: 24 }}
        pb={{ base: 14, md: 22 }}
      >
        <Grid
          templateColumns={{
            base: "1fr",
            lg: "minmax(260px, 0.72fr) minmax(0, 1.28fr)",
          }}
          gap={{ base: 8, lg: 16 }}
          alignItems="start"
          pb={{ base: 12, md: 16 }}
          borderBottom="1px solid"
          borderColor="blackAlpha.200"
        >
          <MaskedReveal>
            <Heading
              as="h1"
              fontSize={{ base: "3xl", md: "5xl" }}
              lineHeight="1.2"
              letterSpacing="-0.04em"
              pb="0.1em"
            >
              {t("about.title")}
            </Heading>
          </MaskedReveal>
          <MaskedReveal delay={0.1}>
            <Box
              data-testid="about-background-copy"
              maxW="68ch"
              color="gray.700"
              fontSize={{ base: "sm", md: "md" }}
              lineHeight={{ base: 1.85, md: 2 }}
              textAlign={{ base: "left", md: "justify" }}
              dangerouslySetInnerHTML={{ __html: t("about.mission.content") }}
            />
          </MaskedReveal>
        </Grid>

        <Grid
          data-testid="about-manifesto"
          templateColumns={{ base: "1fr", md: "repeat(2, minmax(0, 1fr))" }}
          gap={{ base: 10, md: 12 }}
          pt={{ base: 12, md: 16 }}
        >
          <MaskedReveal delay={0.06}>
            <Box
              pl={{ base: 5, md: 6 }}
              borderLeft="2px solid"
              borderColor="primary.400"
            >
              <Heading as="h3" fontSize={{ base: "lg", md: "xl" }} mb={3}>
                {t("about.vision.title")}
              </Heading>
              <Box
                color="gray.700"
                fontSize={{ base: "md", md: "lg" }}
                lineHeight={{ base: 1.7, md: 1.8 }}
                dangerouslySetInnerHTML={{ __html: t("about.vision.content") }}
              />
            </Box>
          </MaskedReveal>
          <MaskedReveal delay={0.14}>
            <Box
              pl={{ base: 5, md: 6 }}
              borderLeft="2px solid"
              borderColor="blue.300"
            >
              <Heading as="h3" fontSize={{ base: "lg", md: "xl" }} mb={3}>
                {t("about.companyMission.title")}
              </Heading>
              <Box
                color="gray.700"
                fontSize={{ base: "md", md: "lg" }}
                lineHeight={{ base: 1.7, md: 1.8 }}
                dangerouslySetInnerHTML={{
                  __html: t("about.companyMission.content"),
                }}
              />
            </Box>
          </MaskedReveal>
        </Grid>
      </Box>

      <Box
        data-testid="about-continuum-background"
        bg="radial-gradient(circle at 8% 32%, rgba(219, 235, 250, 0.38), transparent 34%), radial-gradient(circle at 94% 78%, rgba(255, 232, 235, 0.42), transparent 36%), linear-gradient(180deg, #fafbfd 0%, #f3f8fb 56%, #f8f5f7 100%)"
      >
        <Box
          maxW="1400px"
          mx="auto"
          px={{ base: 4, md: 8 }}
          pb={{ base: 14, md: 24 }}
        >
          <MaskedReveal>
            <Heading
              as="h3"
              fontSize={{ base: "2xl", md: "4xl" }}
              letterSpacing="-0.025em"
              mb={{ base: 8, md: 12 }}
            >
              {t("about.philosophy.title")}
            </Heading>
          </MaskedReveal>
          <MaskedReveal delay={0.12}>
            <Box data-testid="about-timeline" width="100%">
              <TimelineComponent items={timelineItems} />
            </Box>
          </MaskedReveal>

          <Box
            data-testid="about-future"
            mt={{ base: 16, md: 24 }}
            py={{ base: 10, md: 14 }}
            px={{ base: 6, md: 10 }}
            borderTop="1px solid"
            borderBottom="1px solid"
            borderColor="blackAlpha.200"
            bg="radial-gradient(circle at 8% 10%, rgba(219, 235, 250, 0.78), transparent 42%), radial-gradient(circle at 92% 90%, rgba(255, 232, 235, 0.8), transparent 42%), rgba(255,255,255,0.24)"
          >
            <Grid
              templateColumns={{ base: "1fr", md: "0.8fr 1.2fr" }}
              gap={{ base: 5, md: 12 }}
              alignItems="start"
            >
              <MaskedReveal>
                <Heading as="h3" fontSize={{ base: "2xl", md: "4xl" }}>
                  {t("about.future.title")}
                </Heading>
              </MaskedReveal>
              <MaskedReveal delay={0.12}>
                <Text
                  maxW="68ch"
                  color="gray.700"
                  fontSize={{ base: "sm", md: "md" }}
                  lineHeight={{ base: 1.8, md: 1.95 }}
                  textAlign={{ base: "left", md: "justify" }}
                >
                  {t("about.future.content")}
                </Text>
              </MaskedReveal>
            </Grid>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default About;
